import type { CustomerConfig } from '@/entities/customer-config';
import { FeatureFlag } from '@/types/feature-flag';

export type WarrantyVersion = 'v3' | 'v2' | 'v1';

export type ActiveWarrantyInfo = {
  version: WarrantyVersion;
  featureFlag: keyof CustomerConfig['featureFlags'];
};

/**
 * Determines the active warranty version based on feature flags.
 * This is the SINGLE SOURCE OF TRUTH for warranty version resolution.
 *
 * Priority: warrantyV3 > warrantyV2 > warranty (v1)
 *
 * Returns null if no warranty feature is enabled.
 */
export function getActiveWarrantyVersion(
  featureFlags: CustomerConfig['featureFlags'],
  isInternal: boolean,
): ActiveWarrantyInfo | null {
  const isEnabled = (flag: FeatureFlag) =>
    flag === FeatureFlag.PUBLIC ||
    (flag === FeatureFlag.INTERNAL && isInternal);

  if (isEnabled(featureFlags.warrantyV3)) {
    return { version: 'v3', featureFlag: 'warrantyV3' };
  }

  if (isEnabled(featureFlags.warrantyV2)) {
    return { version: 'v2', featureFlag: 'warrantyV2' };
  }

  if (isEnabled(featureFlags.warranty)) {
    return { version: 'v1', featureFlag: 'warranty' };
  }

  return null;
}

/**
 * Checks if ANY warranty feature is enabled (regardless of version).
 * Useful for determining sidebar visibility without caring about version.
 */
export function isWarrantyEnabled(
  featureFlags: CustomerConfig['featureFlags'],
  isInternal: boolean,
): boolean {
  return getActiveWarrantyVersion(featureFlags, isInternal) !== null;
}

const VERSION_LABELS: Record<WarrantyVersion, string> = {
  v3: 'Warranty V3',
  v2: 'Warranty V2',
  v1: 'Warranty (legacy)',
};

/**
 * Generates a tooltip message explaining the warranty version state.
 *
 * Note: This is only displayed when AppSidebarInternalIcon renders,
 * which only happens when the active feature flag is INTERNAL.
 */
export function getWarrantyInternalTooltip(
  featureFlags: CustomerConfig['featureFlags'],
  tenant: string,
): string {
  const internalView = getActiveWarrantyVersion(featureFlags, true);
  const externalView = getActiveWarrantyVersion(featureFlags, false);

  if (!internalView) {
    return `INTERNAL - not yet available for ${tenant}`;
  }

  const internalLabel = VERSION_LABELS[internalView.version];

  if (externalView === null) {
    return `INTERNAL: You see ${internalLabel}, but ${tenant} has no warranty access yet`;
  }

  if (internalView.version !== externalView.version) {
    const externalLabel = VERSION_LABELS[externalView.version];
    return `⚠️ VERSION MISMATCH: You see ${internalLabel}, but ${tenant} sees ${externalLabel}`;
  }

  return `INTERNAL - not yet available for ${tenant}`;
}
